import { Button, CircularProgress, Switch, Backdrop } from "@mui/material";
import Select from "react-select";
import { ReactComponent as SaveIcon } from "../assets/icons/CompanyEditPage/SaveIcon.svg";
import { ReactComponent as UploadIcon } from "../assets/icons/Cloud_Upload.svg";
import { ReactComponent as TitleIcon } from "../assets/icons/EventCreation/TitleIcon.svg";
import { ReactComponent as ChevronLeftIcon } from "../assets/icons/EventCreation/ChevronLeftIcon.svg";
import LanguageSelector from "../components/LanguageSelector/index.jsx";
import NotificationBell from "../components/NotificationBell/index.jsx";
import AddBreakfastPartnersModal from "../modals/AddBreakfastPartners";
import { generateAssetUrl } from "../utils/common.js";
import AssetImage from "../components/AssetImage";
import { getEventCreationCategorySelectStyles } from "../utils/eventCreation/eventCreationCategorySelectStyles.js";
import styles from "../styles/BreakfastEventForm.module.css";
import {
  includesMembers,
  includesNonMembers,
  isAudienciaChosen,
} from "./breakfastEventFormModel.js";

/**
 * Loading scrim. The z-index sits above the drawer, and the progress color matches the rest of the app.
 * Open while the form is loading an existing event or while a save is in flight.
 */
const BACKDROP_SX = {
  color: "#fff",
  zIndex: (theme) => theme.zIndex.drawer + 1,
  backgroundColor: "rgba(0, 0, 0, 0.5)",
  backdropFilter: "blur(4px)",
};
const PROGRESS_SX = { color: "#4eabe7" };
const PROGRESS_SIZE = 60;
const PROGRESS_THICKNESS = 4;
const BUTTON_PROGRESS_SIZE = 24;
const BROWSE_LINK_STYLE = { color: "#1D5AE8" };
const FIELD_BORDER_DEFAULT = "#909092";
const FIELD_BORDER_ERROR = "red";

/**
 * Host dropdown reuses the event-creation select styles and only adds a short menu.
 * The selected option is computed in the host section so the select value is not an inline arrow.
 */
const HOST_MENU_MAX_HEIGHT = "110px";
const hostSelectStyles = {
  ...getEventCreationCategorySelectStyles(false, "host"),
  menu: (baseStyles) => ({
    ...baseStyles,
    zIndex: 2,
    maxHeight: HOST_MENU_MAX_HEIGHT,
    overflowY: "auto",
  }),
  menuList: (baseStyles) => ({
    ...baseStyles,
    zIndex: 2,
    maxHeight: HOST_MENU_MAX_HEIGHT,
    overflowY: "auto",
    padding: "4px 0",
  }),
};

/**
 * Host label shows the person's name and email. The option value is the user id stored as `idUsuarioAnfitrion`.
 */
const formatHostOption = (user) => (
  <span>{`${user.nombre} (${user.correoElectronico})`}</span>
);
const hostOptionValue = (user) => user._id;

/**
 * Text fields that do not use the CSS error class turn the border red when save was attempted and the value is empty.
 */
const fieldBorderStyle = (showError, value) => ({
  borderColor: showError && !value ? FIELD_BORDER_ERROR : FIELD_BORDER_DEFAULT,
});

/** Label plus the red asterisk used by every required breakfast field. */
const RequiredLabel = ({ children }) => (
  <label className={styles.label}>
    {children}
    <span className={styles.required}> *</span>
  </label>
);

/**
 * Date input. An empty API value is shown as an empty string so the control stays controlled.
 */
const BreakfastDateField = ({
  label,
  name,
  value,
  onChange,
  className = styles.eventDateCol,
  inputClassName = styles.input,
}) => (
  <div className={className}>
    <RequiredLabel>{label}</RequiredLabel>
    <input
      type="date"
      name={name}
      className={inputClassName}
      value={value || ""}
      onChange={onChange}
    />
  </div>
);

/**
 * Single-line text or number field. Capacity uses `min` 0 and `step` 1; other fields omit those attributes.
 */
const BreakfastTextField = ({
  label,
  name,
  value,
  onChange,
  placeholder,
  showError,
  className,
  type = "text",
  inputClassName = styles.input,
  min,
  step,
}) => (
  <div className={className}>
    <RequiredLabel>{label}</RequiredLabel>
    <input
      type={type}
      name={name}
      value={value || ""}
      onChange={onChange}
      className={inputClassName}
      style={fieldBorderStyle(showError, value)}
      placeholder={placeholder}
      min={min}
      step={step}
    />
  </div>
);

/** Blocks the page while the event is loading or the save request is running. */
const BreakfastFormBackdrop = ({ open }) => (
  <Backdrop sx={BACKDROP_SX} open={open}>
    <CircularProgress
      size={PROGRESS_SIZE}
      thickness={PROGRESS_THICKNESS}
      sx={PROGRESS_SX}
    />
  </Backdrop>
);

/** Page title and the back button. Back uses history, not a hard-coded path. */
const BreakfastFormHeader = ({ t, navigate, onGoBack }) => (
  <div className={styles.header}>
    <div className={styles.languages}>
      <NotificationBell navigate={navigate} />
      <LanguageSelector />
    </div>
    <div className={styles.backNavigation}>
      <button type="button" className={styles.backButton} onClick={onGoBack}>
        <ChevronLeftIcon />
        <span>{t("common.goBack")}</span>
      </button>
    </div>
    <div className={styles.headerInfoContainer}>
      <div className={styles.headerInfo}>
        <TitleIcon className={styles.titleIcon} />
        <h1 className={styles.title}>{t("pages.breakfastEventForm.header")}</h1>
      </div>
    </div>
  </div>
);

/**
 * Host and the date the host can still open the event.
 * The section title uses the speaker translation key; that copy is already what the form shipped with.
 */
const BreakfastHostSection = ({
  t,
  formData,
  userHosts,
  onHostChange,
  onInputChange,
}) => {
  const selectedHost = userHosts.find(
    (user) => user._id === formData.idUsuarioAnfitrion,
  );
  return (
  <>
    <div className={styles.sectionTitle}>
      <span>{t("pages.breakfastEventForm.speaker")}</span>
    </div>
    <div className={styles.formGroup}>
      <div className={styles.formRow}>
        <div className={styles.hostCol}>
          <RequiredLabel>{t("pages.breakfastEventForm.host")}</RequiredLabel>
          <Select
            options={userHosts}
            value={selectedHost}
            onChange={onHostChange}
            placeholder={t("pages.breakfastEventForm.hostPlaceholder")}
            styles={hostSelectStyles}
            formatOptionLabel={formatHostOption}
            getOptionValue={hostOptionValue}
          />
        </div>
        <BreakfastDateField
          label={t("pages.breakfastEventForm.accessLimit")}
          name="fechaLimiteAccesoAnfitrion"
          value={formData.fechaLimiteAccesoAnfitrion}
          onChange={onInputChange}
          className={styles.accessLimitCol}
        />
      </div>
    </div>
  </>
  );
};

/**
 * Banner drop zone. Edit mode shows the current image even before a replacement file is chosen.
 * `error` is the file-type or file-size message, not the save-validation highlight.
 */
const BreakfastBanner = ({
  t,
  formData,
  imageRef,
  isEditMode,
  error,
  onDragEnter,
  onDragLeave,
  onDrop,
  onImageChange,
}) => (
  <div className={styles.imageContainer}>
    <RequiredLabel>{t("pages.breakfastEventForm.eventBanner")}</RequiredLabel>
    <div
      ref={imageRef}
      className={styles.dropFileInput}
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {isEditMode || formData.imagenBanner ? (
        <AssetImage
          className={styles.uploadedImage}
          src={generateAssetUrl(formData.imagenBanner)}
          alt=""
        />
      ) : (
        <div className={styles.uploadContainer}>
          <UploadIcon className={styles.uploadIcon} />
          {error && <span className={styles.errorMessage}>{error}</span>}
          <span className={styles.uploadTitle}>{t("common.drag&drop")}</span>
          <span className={styles.uploadSubtitle}>
            {t("common.yourImage")}{" "}
            <span style={BROWSE_LINK_STYLE} className={styles.uploadSubtitle}>
              {t("common.browseFile")}
            </span>
          </span>
        </div>
      )}
      <input
        className={styles.imageInput}
        type="file"
        accept="image/*"
        onChange={onImageChange}
      />
    </div>
  </div>
);

/**
 * Two checkboxes write one `audiencia` value: members, non-members, or both.
 * Unchecking both stores null, and the partner button stays disabled until one side is chosen.
 * The active switch is independent of audience.
 */
const BreakfastAudienceFields = ({
  t,
  formData,
  onSociosAudience,
  onNonMembersAudience,
  onActivoChange,
}) => (
  <>
    <div className={styles.audienciaBelowBanner}>
      <label className={styles.label}>
        {t("pages.breakfastEventForm.audience")}
      </label>
      <div className={styles.audienciaCheckboxRow}>
        <label className={styles.audienciaCheckboxLabel}>
          <input
            type="checkbox"
            checked={includesMembers(formData.audiencia)}
            onChange={onSociosAudience}
          />
          <span>{t("pages.breakfastEventForm.audienceSocios")}</span>
        </label>
        <label className={styles.audienciaCheckboxLabel}>
          <input
            type="checkbox"
            checked={includesNonMembers(formData.audiencia)}
            onChange={onNonMembersAudience}
          />
          <span>{t("pages.breakfastEventForm.audienceNonMembers")}</span>
        </label>
      </div>
    </div>
    <div className={styles.activoBelowBanner}>
      <label className={styles.label}>
        {t("pages.breakfastEventForm.activateEvent")}
      </label>
      <Switch checked={Boolean(formData.activo)} onChange={onActivoChange} />
    </div>
  </>
);

/**
 * Event name is checked against other breakfast events. A duplicate name blocks save even if every other field is filled.
 */
const BreakfastNameAndDate = ({
  t,
  formData,
  showError,
  nameValidation,
  onInputChange,
}) => {
  const nameInvalid =
    (showError && !formData.nombre) || !nameValidation.isValid;
  const dateInvalid = showError && !formData.fechaEvento;
  return (
    <div className={`${styles.formRow} ${styles.rowEventName}`}>
      <div className={styles.nameCol}>
        <RequiredLabel>{t("pages.breakfastEventForm.eventName")}</RequiredLabel>
        <input
          type="text"
          name="nombre"
          value={formData.nombre}
          onChange={onInputChange}
          className={`${styles.input} ${nameInvalid ? styles.inputError : ""}`}
          placeholder={t("pages.breakfastEventForm.eventNamePlaceholder")}
        />
        {!nameValidation.isValid && (
          <div className={styles.validationMessage}>
            {t("pages.breakfastEventForm.duplicateEventNameError")}
          </div>
        )}
      </div>
      <BreakfastDateField
        label={t("pages.breakfastEventForm.eventDate")}
        name="fechaEvento"
        value={formData.fechaEvento}
        onChange={onInputChange}
        inputClassName={`${styles.input} ${dateInvalid ? styles.inputError : ""}`}
      />
    </div>
  );
};

/** Description is required. The error style is the textarea class, not a red inline border. */
const BreakfastDescription = ({ t, formData, showError, onInputChange }) => {
  const descriptionInvalid = showError && !formData.descripcion;
  return (
    <div className={styles.formRow}>
      <div className={styles.descriptionCol}>
        <RequiredLabel>
          {t("pages.breakfastEventForm.description")}
        </RequiredLabel>
        <textarea
          name="descripcion"
          value={formData.descripcion ?? ""}
          onChange={onInputChange}
          className={`${styles.textarea} ${descriptionInvalid ? styles.textareaError : ""}`}
          placeholder={t("pages.breakfastEventForm.descriptionPlaceholder")}
        />
      </div>
    </div>
  );
};

/**
 * Start time is a native time input. Duration is free text `H:MM` (0:00 through 24:59), shown with an hours suffix.
 * The placeholder `0:00` is not a valid duration; the event must last at least one minute.
 */
const BreakfastTimeRow = ({ t, formData, showError, onInputChange }) => {
  const timeInvalid = showError && !formData.horaInicio;
  return (
    <div className={styles.formRow}>
      <div className={styles.timeCol}>
        <RequiredLabel>{t("pages.b2bEventForm.startTime")}</RequiredLabel>
        <input
          type="time"
          name="horaInicio"
          value={formData.horaInicio || ""}
          onChange={onInputChange}
          className={`${styles.input} ${timeInvalid ? styles.inputError : ""}`}
        />
      </div>
      <div className={styles.durationCol}>
        <RequiredLabel>{t("pages.breakfastEventForm.duration")}</RequiredLabel>
        <div className={styles.durationInputContainer}>
          <input
            type="text"
            name="duracion"
            pattern="([0-9]|1[0-9]|2[0-4]):[0-5][0-9]"
            value={formData.duracion || "0:00"}
            onChange={onInputChange}
            className={styles.input}
            placeholder={t("common.durationPlaceholder")}
          />
          <span className={styles.durationUnit}>
            {t("pages.b2bEventForm.hoursShort")}
          </span>
        </div>
      </div>
    </div>
  );
};

/**
 * Three registration dates: when members can sign up, when non-members can, and the last registration day.
 */
const BreakfastAvailabilityRow = ({ t, formData, onInputChange }) => (
  <div className={`${styles.formRow} ${styles.rowThreeDateCols}`}>
    <BreakfastDateField
      label={t("pages.breakfastEventForm.membersDate")}
      name="fechaDisponibleSocios"
      value={formData.fechaDisponibleSocios}
      onChange={onInputChange}
    />
    <BreakfastDateField
      label={t("pages.breakfastEventForm.nonMembersDate")}
      name="fechaDisponibleNoSocios"
      value={formData.fechaDisponibleNoSocios}
      onChange={onInputChange}
      className={`${styles.eventDateCol} ${styles.nonMembersDateCol}`}
    />
    <BreakfastDateField
      label={t("pages.breakfastEventForm.dueDateRegistration")}
      name="fechaLimiteRegistro"
      value={formData.fechaLimiteRegistro}
      onChange={onInputChange}
    />
  </div>
);

/**
 * Location and maximum capacity. Capacity may be 0; an empty field is what the red border is for.
 */
const BreakfastLocationRow = ({ t, formData, showError, onInputChange }) => (
  <div className={styles.formRow}>
    <BreakfastTextField
      label={t("pages.breakfastEventForm.location")}
      name="ubicacion"
      value={formData.ubicacion}
      onChange={onInputChange}
      showError={showError}
      placeholder={t("pages.breakfastEventForm.locationPlaceholder")}
      className={styles.locationCol}
    />
    <BreakfastTextField
      label={t("pages.breakfastEventForm.maximumCapacity")}
      name="cupoMaximo"
      type="number"
      min={0}
      step={1}
      value={formData.cupoMaximo}
      onChange={onInputChange}
      showError={showError}
      placeholder={t("pages.breakfastEventForm.maximumCapacityPlaceholder")}
      className={styles.formColTwo}
    />
  </div>
);

/** Name, schedule, location, and capacity column beside the banner. */
const BreakfastDetails = (props) => (
  <div className={styles.detailsContainer}>
    <BreakfastNameAndDate {...props} />
    <BreakfastDescription {...props} />
    <BreakfastTimeRow {...props} />
    <BreakfastAvailabilityRow {...props} />
    <BreakfastLocationRow {...props} />
  </div>
);

/** Banner, audience, active switch, and the event detail fields. */
const BreakfastGeneralSection = (props) => (
  <>
    <div className={styles.sectionTitle}>
      <span>{props.t("pages.breakfastEventForm.generalInformation")}</span>
    </div>
    <div className={styles.formGroupOne}>
      <div className={styles.formGroupOneLeftStack}>
        <BreakfastBanner {...props} />
        <BreakfastAudienceFields {...props} />
      </div>
      <BreakfastDetails {...props} />
    </div>
  </>
);

/** Speaker name, job, company, and the conference title. All four are required. */
const BreakfastSpeakerSection = ({ t, formData, showError, onInputChange }) => (
  <>
    <div className={styles.sectionTitle}>
      <span>{t("pages.breakfastEventForm.speaker")}</span>
    </div>
    <div className={styles.formGroup}>
      <div className={styles.formRow}>
        <BreakfastTextField
          label={t("pages.breakfastEventForm.speakerName")}
          name="nombreConferencista"
          value={formData.nombreConferencista}
          onChange={onInputChange}
          showError={showError}
          placeholder={t("pages.breakfastEventForm.speakerNamePlaceholder")}
          className={styles.speakerNameCol}
        />
        <BreakfastTextField
          label={t("pages.breakfastEventForm.speakerPosition")}
          name="puestoConferencista"
          value={formData.puestoConferencista}
          onChange={onInputChange}
          showError={showError}
          placeholder={t("pages.breakfastEventForm.speakerPositionPlaceholder")}
          className={styles.speakerPositionCol}
        />
        <BreakfastTextField
          label={t("pages.breakfastEventForm.speakerCompany")}
          name="nombreEmpresaConferencista"
          value={formData.nombreEmpresaConferencista}
          onChange={onInputChange}
          showError={showError}
          placeholder={t("pages.breakfastEventForm.speakerCompanyPlaceholder")}
          className={styles.speakerCompanyCol}
        />
      </div>
      <div className={styles.formRow}>
        <BreakfastTextField
          label={t("pages.breakfastEventForm.conferenceTitle")}
          name="tituloConferencia"
          value={formData.tituloConferencia}
          onChange={onInputChange}
          showError={showError}
          placeholder={t("pages.breakfastEventForm.conferenceTitlePlaceholder")}
          className={styles.titleCol}
        />
      </div>
    </div>
  </>
);

/**
 * Shared publish and partner button. While `loading` is true the label is replaced by a spinner and the save icon is hidden.
 */
const BreakfastLoadingButton = ({ loading, label, disabled, type, onClick }) => (
  <Button
    type={type}
    variant="contained"
    onClick={onClick}
    className={styles.saveButton}
    disabled={disabled}
    startIcon={loading ? null : <SaveIcon />}
  >
    {loading ? (
      <CircularProgress size={BUTTON_PROGRESS_SIZE} color="inherit" />
    ) : (
      label
    )}
  </Button>
);

/**
 * Opens the partner modal. Disabled until an audience is selected, because partner filters depend on that choice.
 */
const BreakfastPartnersButton = ({ t, loading, formData, onOpenPartners }) => (
  <>
    <div className={styles.sectionTitle}>
      <span>{t("pages.breakfastEventForm.guestPartners")}</span>
    </div>
    <BreakfastLoadingButton
      type="button"
      loading={loading}
      label={t("pages.breakfastEventForm.addNewPartner")}
      disabled={loading || !isAudienciaChosen(formData.audiencia)}
      onClick={onOpenPartners}
    />
  </>
);

/**
 * Submit stays disabled while saving, while the name is a duplicate, or while required fields are missing.
 * Editing an existing event also requires an unsaved change.
 */
const BreakfastSaveSection = ({ t, loading, saveDisabled }) => (
  <div className={styles.saveSectionContainer}>
    <div className={styles.saveSectionContent}>
      <div className={styles.messageContainer}>
        {t("pages.breakfastEventForm.publishInstructions")}
      </div>
      <BreakfastLoadingButton
        type="submit"
        loading={loading}
        label={t("pages.breakfastEventForm.publishEvent")}
        disabled={saveDisabled}
      />
    </div>
  </div>
);

/**
 * Composes the breakfast form. Handlers arrive already memoized from `useBreakfastEventForm`.
 * The partner modal sits outside the form so its buttons do not submit the event.
 *
 * Drag enter and drag leave only toggle a CSS class on the drop zone. They do not read the file.
 * The file input's change handler does. Edit mode shows the current banner through `generateAssetUrl`
 * even when the value is a stored path rather than a data URL.
 *
 * Member and non-member checkboxes both look selected when `audiencia` is 2 (both).
 * The publish control is a submit button. The partner control is type="button" so it cannot submit the form.
 */
const BreakfastEventFormView = ({
  t,
  navigate,
  loading,
  saving,
  formData,
  userHosts,
  handleGoBack,
  handleHostChange,
  handleInputChange,
  imageRef,
  onDragEnter,
  onDragLeave,
  onDrop,
  isEditMode,
  error,
  handleImageChange,
  showError,
  nameValidation,
  handleSociosAudience,
  handleNonMembersAudience,
  handleActivoChange,
  handleSubmit,
  saveDisabled,
  handleOpenPartners,
  showAddPartnersModal,
  handleClosePartners,
  handlePartnersSuccess,
}) => (
  <div className={styles.container}>
    <BreakfastFormBackdrop open={loading || saving} />
    <BreakfastFormHeader t={t} navigate={navigate} onGoBack={handleGoBack} />
    <div className={styles.formContainer}>
      <form onSubmit={handleSubmit} className={styles.eventForm}>
        <div className={styles.formGroups}>
          <BreakfastHostSection
            t={t}
            formData={formData}
            userHosts={userHosts}
            onHostChange={handleHostChange}
            onInputChange={handleInputChange}
          />
          <BreakfastGeneralSection
            t={t}
            formData={formData}
            imageRef={imageRef}
            isEditMode={isEditMode}
            error={error}
            onDragEnter={onDragEnter}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onImageChange={handleImageChange}
            onSociosAudience={handleSociosAudience}
            onNonMembersAudience={handleNonMembersAudience}
            onActivoChange={handleActivoChange}
            showError={showError}
            nameValidation={nameValidation}
            onInputChange={handleInputChange}
          />
          <BreakfastSpeakerSection
            t={t}
            formData={formData}
            showError={showError}
            onInputChange={handleInputChange}
          />
          <BreakfastPartnersButton
            t={t}
            loading={loading}
            formData={formData}
            onOpenPartners={handleOpenPartners}
          />
        </div>
        <BreakfastSaveSection
          t={t}
          loading={loading}
          saveDisabled={saveDisabled}
        />
      </form>
      <AddBreakfastPartnersModal
        open={showAddPartnersModal}
        eventData={formData}
        onCancel={handleClosePartners}
        onSuccess={handlePartnersSuccess}
      />
    </div>
  </div>
);

export default BreakfastEventFormView;
